import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import {
  usePlayerActivitySummaryQuery,
  usePlayerSessionSummaryQuery,
} from "@/hooks/api/useReportQuery"

/**
 * 액티비티/세션 모드에 맞는 선수 요약 API 하나만 호출한다 (일감 #75 · #177).
 *
 * 액티비티 모드는 액티비티 전체 시간, 세션 모드는 선택 세션 구간만 집계하므로 두 API 값은 원래 다르다.
 * 리포트 화면과 PDF 빌더가 이 훅 하나를 쓰면 같은 조건에서 같은 값이 나온다.
 * 반대편 모드의 쿼리는 비활성으로 두어 불필요한 호출과 잔여 데이터 오용을 막는다.
 */
export function usePlayerSummaryByMode({
  mode,
  activityId,
  sessionIds,
  playerIds,
  enabled = true,
}: {
  mode: ACTIVITY_SESSION_TYPE
  activityId: number | null | undefined
  sessionIds: number[]
  playerIds: number[]
  enabled?: boolean
}) {
  const isActivityMode = mode === ACTIVITY_SESSION_TYPE.ACTIVITY
  const hasPlayers = playerIds.length > 0

  const activityQuery = usePlayerActivitySummaryQuery(
    { activities: activityId ?? 0, players: playerIds },
    enabled && isActivityMode && !!activityId && hasPlayers,
  )
  const sessionQuery = usePlayerSessionSummaryQuery(
    { sessions: sessionIds, players: playerIds },
    enabled && !isActivityMode && sessionIds.length > 0 && hasPlayers,
  )

  return {
    isActivityMode,
    playerActivitySum: activityQuery.data,
    playerSessionSum: sessionQuery.data,
    /** 현재 모드의 요약. 반대편 모드 데이터는 섞이지 않는다 */
    playerSummary: (isActivityMode ? activityQuery.data : sessionQuery.data) ?? null,
    isLoading: activityQuery.isLoading || sessionQuery.isLoading,
  }
}
